import { useEffect, useId, useRef, useState } from 'react';

import type {
  StripeCardCvcElement,
  StripeCardCvcElementChangeEvent,
  StripeCardCvcElementOptions,
  StripeCardExpiryElement,
  StripeCardExpiryElementChangeEvent,
  StripeCardExpiryElementOptions,
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
  StripeCardNumberElementOptions,
} from '@stripe/stripe-js';

import { useStripeElements } from '../../StripeProviders';
import { createStripeElement, getStripeElement } from './utils';

export type SupportedStripeElementType =
  | 'cardNumber'
  | 'cardCvc'
  | 'cardExpiry';

/** Type for the change event according to the element type. */
export type StripeChangeEvent<TType extends SupportedStripeElementType> =
  TType extends 'cardNumber'
    ? StripeCardNumberElementChangeEvent
    : TType extends 'cardCvc'
      ? StripeCardCvcElementChangeEvent
      : StripeCardExpiryElementChangeEvent;

/** Type of the supported Stripe elements. */
export type SupportedStripeElement<TType extends SupportedStripeElementType> =
  TType extends 'cardNumber'
    ? StripeCardNumberElement
    : TType extends 'cardCvc'
      ? StripeCardCvcElement
      : StripeCardExpiryElement;

/** Type of Stripe options according to the element type. */
export type StripeElementOptions<TType extends SupportedStripeElementType> =
  TType extends 'cardNumber'
    ? StripeCardNumberElementOptions
    : TType extends 'cardCvc'
      ? StripeCardCvcElementOptions
      : StripeCardExpiryElementOptions;

/** The props for the internal `StripeInput` component. */
export interface StripeInputProps<TType extends SupportedStripeElementType> {
  /** Type of the Stripe element. */
  type: TType;
  /** Optional ID for the container. */
  id?: string;
  /** Optional class for the container. */
  className?: string;
  /** Event handler when the user . */
  onChange?: (
    event: StripeChangeEvent<TType>,
    element: SupportedStripeElement<TType>
  ) => void;
  /** Event handler when the element blurs. */
  onBlur?: (element: SupportedStripeElement<TType>) => void;
  /** Event handler when the element receives focus. */
  onFocus?: (element: SupportedStripeElement<TType>) => void;
  /** Event handler when the element is ready. */
  onReady?: (element: SupportedStripeElement<TType>) => void;
  /** Stripe options to pass to the element. */
  options?: {
    classes?: StripeElementOptions<TType>['classes'];
    style?: StripeElementOptions<TType>['style'];
    placeholder?: StripeElementOptions<TType>['placeholder'];
    disabled?: StripeElementOptions<TType>['disabled'];
  };
}

/**
 * Internal component to render a Stripe Element as input field. Use
 * {@link CreditCardInput} instead.
 *
 * @remarks
 * Must be a child of {@link StripeProvider} and {@link StripeElementsProvider}.
 */
export function StripeInput<TType extends SupportedStripeElementType>(
  props: StripeInputProps<TType>
) {
  const reactId = useId();
  const uniqueId = props.id || reactId;
  const id = uniqueId ? `${props.type}-${uniqueId}` : undefined;

  const stripeElements = useStripeElements();
  const [stripeElement, setStripeElement] =
    useState<SupportedStripeElement<TType> | null>();

  /**
   * Create a stripe element or update an existing stripe element. If the
   * element already exists we can update the options. Each element must belong
   * to an elements set that is created by the StripeContext.
   */
  useEffect(() => {
    if (!stripeElements) {
      return;
    }

    const cachedElement = getStripeElement(stripeElements, props.type);

    if (!stripeElement && cachedElement) {
      cachedElement.clear();
      setStripeElement(cachedElement);
      return;
    }

    if (!stripeElement) {
      setStripeElement(
        createStripeElement<TType>(stripeElements, props.type, props.options)
      );
      return;
    }

    if (props.options) {
      stripeElement.update(props.options);
    }
  }, [stripeElements, stripeElement, props.type, props.options]);

  // After an element is created in-memory it must be mounted to the DOM. This
  // is where the actual iframe is added to the DOM.
  useEffect(() => {
    if (!id || !stripeElement) {
      return;
    }

    // Note: using `#${id}` as a selector here throws an error because
    // the element id contains characters that cannot be used in a selector
    const element = document.getElementById(id);
    if (element) {
      stripeElement.mount(element);
    }

    return (): void => stripeElement.unmount();
  }, [stripeElement, id]);

  // To support react style handler functions they must be stored inside a
  // mutable ref. This is so we only attach a handler once inside each iframe
  // element.
  const eventHandlers = useRef({
    onChange: props.onChange,
    onBlur: props.onBlur,
    onFocus: props.onFocus,
    onReady: props.onReady,
  });

  useEffect(() => {
    eventHandlers.current = {
      onChange: props.onChange,
      onBlur: props.onBlur,
      onFocus: props.onFocus,
      onReady: props.onReady,
    };
  });

  useEffect(() => {
    if (!stripeElement) {
      return;
    }

    const handleChange = (e: unknown) =>
      eventHandlers.current.onChange?.(
        e as StripeChangeEvent<TType>,
        stripeElement
      );

    const handleReady = () => eventHandlers.current.onReady?.(stripeElement);
    const handleFocus = () => eventHandlers.current.onFocus?.(stripeElement);
    const handleBlur = () => eventHandlers.current.onBlur?.(stripeElement);

    switch (props.type) {
      case 'cardNumber': {
        const element = stripeElement as StripeCardNumberElement;
        element.on('change', handleChange);
        element.on('ready', handleReady);
        element.on('focus', handleFocus);
        element.on('blur', handleBlur);
        break;
      }
      case 'cardCvc': {
        const element = stripeElement as StripeCardCvcElement;
        element.on('change', handleChange);
        element.on('ready', handleReady);
        element.on('focus', handleFocus);
        element.on('blur', handleBlur);
        break;
      }
      case 'cardExpiry': {
        const element = stripeElement as StripeCardExpiryElement;
        element.on('change', handleChange);
        element.on('ready', handleReady);
        element.on('focus', handleFocus);
        element.on('blur', handleBlur);
        break;
      }
    }
  }, [stripeElement, eventHandlers, props.type]);

  return <div id={id} data-name={props.type} className={props.className} />;
}
